import type { ChartTileOutcomeSummary } from './tile-protocol.ts';

/** Whether any visible base-chart square has definitively failed. */
export function hasFailedVisibleChartTiles(
  outcomes: Readonly<Record<'chs-enc' | 'noaa-enc', ChartTileOutcomeSummary>>,
  visible: Readonly<Record<'chs-enc' | 'noaa-enc', boolean>>
): boolean {
  return (['chs-enc', 'noaa-enc'] as const).some(
    (id) => visible[id] && outcomes[id].failed > 0
  );
}

/** Whether any visible base-chart square has no recorded final outcome. */
export function hasUnsettledVisibleChartTiles(
  outcomes: Readonly<Record<'chs-enc' | 'noaa-enc', ChartTileOutcomeSummary>>,
  visible: Readonly<Record<'chs-enc' | 'noaa-enc', boolean>>
): boolean {
  return (['chs-enc', 'noaa-enc'] as const).some((id) => {
    const outcome = outcomes[id];
    return visible[id]
      && outcome.total > 0
      && outcome.drawn + outcome.noCoverage + outcome.failed < outcome.total;
  });
}

/** Whether a fully settled visible base-chart grid produced mostly no ink. */
export function hasInklessVisibleChartTiles(
  outcomes: Readonly<Record<'chs-enc' | 'noaa-enc', ChartTileOutcomeSummary>>,
  visible: Readonly<Record<'chs-enc' | 'noaa-enc', boolean>>
): boolean {
  return (['chs-enc', 'noaa-enc'] as const).some((id) => {
    const outcome = outcomes[id];
    return visible[id]
      && outcome.total > 0
      && outcome.drawn + outcome.noCoverage + outcome.failed === outcome.total
      && outcome.noCoverage > outcome.total / 2;
  });
}

export type SlowTilesSnapshot = {
  failed: boolean;
  unsettled: boolean;
  inkless: boolean;
  graceElapsed: boolean;
  dismissed: boolean;
  outage: boolean;
  timedOut: boolean;
};

/** The notice is live status: derive it from the current snapshot, never a prior verdict. */
export function slowTilesNoticeVisible(snapshot: SlowTilesSnapshot): boolean {
  return (snapshot.inkless || (snapshot.graceElapsed && (snapshot.failed || snapshot.unsettled)))
    && !snapshot.dismissed
    && !snapshot.outage
    && !snapshot.timedOut;
}
